/**
 * v4.4.0 — Review Assistant (Tier 2 Smart Assistant).
 *
 * A calm, operator-in-control surface for drafting replies to Review
 * Shield feedback with Claude Sonnet 4.5. The page lists recent open
 * low-rating items (rating ≤ 3) plus any positive items the operator
 * filters to. For each item, the operator clicks "Draft a reply",
 * picks a language, edits the draft, and copies it (or marks the
 * record resolved). NOTHING is auto-sent.
 *
 * Constitutional alignment:
 *   * Calm Contract — supportive copy, no alarm.
 *   * Operator agency — every action is reversible / explicit.
 *   * Trilingual — language switcher is independent of UI locale
 *     (an English-speaking franchise lead can draft an FR reply for
 *     a Québécois guest without changing their own UI language).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Sparkles, RefreshCw, Wand2, Copy, CheckCircle2,
    MessageSquareHeart, Globe, Loader2,
} from "lucide-react";
import { OperationalShell } from "../components/OperationalShell";
import { useAuth } from "../../lib/auth";
import api from "../../lib/api";
import { parseUtc } from '../../lib/datetime';

const LANGUAGES = [
    { code: "en", label: "English" },
    { code: "fr", label: "Français" },
    { code: "es", label: "Español" },
];

const ratingClass = (rating) => {
    if (rating <= 2) return { color: "#FCA5A5", bg: "rgba(248, 113, 113, 0.10)" };
    if (rating === 3) return { color: "#FCD34D", bg: "rgba(251, 191, 36, 0.10)" };
    return { color: "#A7F3D0", bg: "rgba(16, 185, 129, 0.10)" };
};

const formatWhen = (iso) => {
    if (!iso) return "";
    try {
        const d = parseUtc(iso);
        return d.toLocaleString(undefined, {
            month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
};

const ReviewAssistant = () => {
    const { t, i18n } = useTranslation();
    const { user, loading: authLoading } = useAuth();
    const [searchParams] = useSearchParams();

    const [feedback, setFeedback] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [targetLang, setTargetLang] = useState(
        (i18n.language || "en").split("-")[0],
    );
    const [drafting, setDrafting] = useState(false);
    const [draft, setDraft] = useState("");
    const [draftMeta, setDraftMeta] = useState(null); // { tone, model, word_count, language }
    const [resolving, setResolving] = useState(false);

    const loadFeedback = async (silent = false) => {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        try {
            const res = await api.get("/review-shield/feedback", {
                params: { status: "open" },
            });
            const items = Array.isArray(res.data) ? res.data : [];
            // Sort: lowest rating first, then newest.
            items.sort((a, b) => {
                if (a.rating !== b.rating) return a.rating - b.rating;
                return (b.timestamp || "").localeCompare(a.timestamp || "");
            });
            setFeedback(items);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("ob.review_assistant.error_load"));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => { loadFeedback(); /* eslint-disable-next-line */ }, []);

    // v4.8.0 — Deep-link arrival from Review Shield Intelligence. If a
    // `?feedback_id=` is present and that item exists in the loaded
    // feedback list, auto-select it. If it doesn't exist (closed/scope),
    // we simply ignore — no error, no surprise.
    useEffect(() => {
        const fid = searchParams.get("feedback_id");
        if (!fid || !feedback.length) return;
        const match = feedback.find((f) => f.id === fid);
        if (match && selectedId !== fid) {
            setSelectedId(fid);
            setDraft("");
            setDraftMeta(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feedback, searchParams]);

    // Sync target language with UI locale until the operator changes it.
    useEffect(() => {
        const code = (i18n.language || "en").split("-")[0];
        if (["en", "fr", "es"].includes(code)) setTargetLang(code);
    }, [i18n.language]);

    const selected = useMemo(
        () => feedback.find((f) => f.id === selectedId) || null,
        [feedback, selectedId],
    );

    const handleSelect = (item) => {
        setSelectedId(item.id);
        setDraft("");
        setDraftMeta(null);
    };

    const handleDraft = async () => {
        if (!selected) return;
        setDrafting(true);
        try {
            const res = await api.post(
                "/operational-base/review-assistant/draft",
                { feedback_id: selected.id, target_language: targetLang },
            );
            setDraft(res.data.draft || "");
            setDraftMeta({
                tone: res.data.tone,
                model: res.data.model,
                word_count: res.data.word_count,
                language: res.data.language,
            });
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || t("ob.review_assistant.error_draft"),
            );
        } finally {
            setDrafting(false);
        }
    };

    const handleCopy = async () => {
        if (!draft) return;
        try {
            await navigator.clipboard.writeText(draft);
            toast.success(t("ob.review_assistant.copied"));
        } catch {
            toast.error(t("ob.review_assistant.copy_failed"));
        }
    };

    const handleMarkResolved = async () => {
        if (!selected) return;
        setResolving(true);
        try {
            await api.put(
                `/review-shield/feedback/${selected.id}/status`,
                { status: "resolved" },
            );
            toast.success(t("ob.review_assistant.marked_resolved"));
            await loadFeedback(true);
            setSelectedId(null);
            setDraft("");
            setDraftMeta(null);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || t("ob.review_assistant.error_resolve"),
            );
        } finally {
            setResolving(false);
        }
    };

    if (authLoading) return null;
    if (!user) return <Navigate to="/login" replace />;

    return (
        <OperationalShell breadcrumb={[{ label: t("ob.review_assistant.eyebrow") }]}>
            <header
                className="mb-10 lg:mb-14 flex items-start justify-between gap-6 flex-wrap"
                data-testid="ob-review-assistant-header"
            >
                <div className="space-y-3 min-w-0">
                    <p className="ob-eyebrow">{t("ob.review_assistant.eyebrow")}</p>
                    <h1
                        className="text-4xl sm:text-5xl flex items-center gap-3"
                        style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}
                        data-testid="ob-review-assistant-title"
                    >
                        <MessageSquareHeart size={28} strokeWidth={1.5} className="text-emerald-300" />
                        {t("ob.review_assistant.title")}
                    </h1>
                    <p
                        className="text-base max-w-2xl"
                        style={{ color: "var(--ob-text-secondary)" }}
                        data-testid="ob-review-assistant-subtitle"
                    >
                        {t("ob.review_assistant.subtitle")}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => loadFeedback(true)}
                    disabled={refreshing}
                    data-testid="ob-review-assistant-refresh"
                    className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 disabled:opacity-50"
                    style={{
                        color: "var(--ob-text-secondary)",
                        border: "1px solid rgba(255,255,255,0.08)",
                    }}
                >
                    <RefreshCw size={12} strokeWidth={1.5} className={refreshing ? "animate-spin" : ""} />
                    {t("ob.review_assistant.refresh")}
                </button>
            </header>

            {loading ? (
                <p
                    className="text-sm"
                    style={{ color: "var(--ob-text-tertiary)" }}
                    data-testid="ob-review-assistant-loading"
                >
                    {t("ob.review_assistant.loading")}
                </p>
            ) : feedback.length === 0 ? (
                <div
                    className="ob-card p-8 sm:p-10 text-center max-w-2xl"
                    data-testid="ob-review-assistant-empty"
                >
                    <Sparkles size={20} strokeWidth={1.5} className="text-emerald-300 mx-auto mb-3" />
                    <p
                        className="text-base mb-2"
                        style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}
                    >
                        {t("ob.review_assistant.empty_title")}
                    </p>
                    <p className="text-sm" style={{ color: "var(--ob-text-secondary)" }}>
                        {t("ob.review_assistant.empty_subtitle")}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                    {/* Feedback list */}
                    <div className="lg:col-span-2 space-y-3" data-testid="ob-review-assistant-list">
                        {feedback.map((item) => (
                            <FeedbackCard
                                key={item.id}
                                item={item}
                                active={item.id === selectedId}
                                onClick={() => handleSelect(item)}
                            />
                        ))}
                    </div>

                    {/* Drafting panel */}
                    <div className="lg:col-span-3" data-testid="ob-review-assistant-panel">
                        {!selected ? (
                            <div
                                className="ob-card p-8 sm:p-10 text-center"
                                data-testid="ob-review-assistant-no-selection"
                            >
                                <p className="text-base mb-2" style={{ color: "var(--ob-text-primary)" }}>
                                    {t("ob.review_assistant.no_selection_title")}
                                </p>
                                <p className="text-sm" style={{ color: "var(--ob-text-secondary)" }}>
                                    {t("ob.review_assistant.no_selection_subtitle")}
                                </p>
                            </div>
                        ) : (
                            <DraftingPanel
                                t={t}
                                feedback={selected}
                                targetLang={targetLang}
                                onLangChange={setTargetLang}
                                onDraft={handleDraft}
                                drafting={drafting}
                                draft={draft}
                                onDraftChange={setDraft}
                                draftMeta={draftMeta}
                                onCopy={handleCopy}
                                onMarkResolved={handleMarkResolved}
                                resolving={resolving}
                            />
                        )}
                    </div>
                </div>
            )}
        </OperationalShell>
    );
};


const FeedbackCard = ({ item, active, onClick }) => {
    const rc = ratingClass(item.rating);
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`ob-review-assistant-item-${item.id}`}
            data-active={active ? "true" : "false"}
            className="w-full text-left ob-card p-4 sm:p-5 transition-colors"
            style={{
                border: active
                    ? "1px solid rgba(16,185,129,0.40)"
                    : "1px solid rgba(255,255,255,0.06)",
                background: active
                    ? "rgba(16,185,129,0.06)"
                    : undefined,
            }}
        >
            <div className="flex items-center justify-between mb-2">
                <span
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ color: rc.color, background: rc.bg }}
                    data-testid="ob-review-assistant-item-rating"
                >
                    {item.rating} / 5
                </span>
                <span className="text-xs" style={{ color: "var(--ob-text-tertiary)" }}>
                    {formatWhen(item.timestamp)}
                </span>
            </div>
            <p
                className="text-sm leading-relaxed mb-2 line-clamp-3"
                style={{ color: "var(--ob-text-primary)" }}
                data-testid="ob-review-assistant-item-message"
            >
                {item.message || <em style={{ color: "var(--ob-text-tertiary)" }}>No message left.</em>}
            </p>
            <div className="flex items-center gap-3 text-xs" style={{ color: "var(--ob-text-tertiary)" }}>
                <span className="truncate">{item.location_name || "—"}</span>
                {item.category && (
                    <span className="px-1.5 py-0.5 rounded uppercase tracking-wider text-[10px]"
                        style={{ background: "rgba(255,255,255,0.04)" }}>
                        {item.category}
                    </span>
                )}
            </div>
        </button>
    );
};


const DraftingPanel = ({
    t, feedback, targetLang, onLangChange,
    onDraft, drafting, draft, onDraftChange, draftMeta,
    onCopy, onMarkResolved, resolving,
}) => {
    const rc = ratingClass(feedback.rating);
    return (
        <div className="ob-card p-6 sm:p-8 space-y-6" data-testid="ob-review-assistant-drafting" data-insight-id="review-shield.assistant.drafting-panel" data-insight-label="Reply drafting panel">
            {/* Context */}
            <div>
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <span
                        className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{ color: rc.color, background: rc.bg }}
                    >
                        {feedback.rating} / 5
                    </span>
                    <span className="text-xs" style={{ color: "var(--ob-text-tertiary)" }}>
                        {feedback.location_name || "—"} · {formatWhen(feedback.timestamp)}
                    </span>
                </div>
                <p
                    className="text-base leading-relaxed"
                    style={{ color: "var(--ob-text-primary)" }}
                    data-testid="ob-review-assistant-feedback-message"
                >
                    {feedback.message || (
                        <em style={{ color: "var(--ob-text-tertiary)" }}>
                            {t("ob.review_assistant.no_message")}
                        </em>
                    )}
                </p>
            </div>

            {/* Language selector + draft trigger */}
            <div className="flex items-center gap-3 flex-wrap pt-4" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <div
                    className="inline-flex items-center gap-2 text-xs"
                    style={{ color: "var(--ob-text-tertiary)" }}
                >
                    <Globe size={12} strokeWidth={1.5} />
                    {t("ob.review_assistant.reply_in")}
                </div>
                <div className="inline-flex rounded-full p-0.5" style={{ border: "1px solid rgba(255,255,255,0.08)" }} data-insight-id="review-shield.assistant.language-picker" data-insight-label="Reply language">
                    {LANGUAGES.map((lng) => (
                        <button
                            key={lng.code}
                            type="button"
                            onClick={() => onLangChange(lng.code)}
                            data-testid={`ob-review-assistant-lang-${lng.code}`}
                            data-active={targetLang === lng.code ? "true" : "false"}
                            className="text-xs px-3 py-1 rounded-full transition-colors"
                            style={{
                                color: targetLang === lng.code ? "#A7F3D0" : "var(--ob-text-tertiary)",
                                background: targetLang === lng.code ? "rgba(16,185,129,0.10)" : "transparent",
                            }}
                        >
                            {lng.label}
                        </button>
                    ))}
                </div>
                <div className="flex-1" />
                <button
                    type="button"
                    onClick={onDraft}
                    disabled={drafting}
                    data-testid="ob-review-assistant-draft-btn"
                    data-insight-id="review-shield.assistant.draft-action"
                    data-insight-label="Generate AI draft"
                    className="inline-flex items-center gap-2 text-sm rounded-full px-4 py-2 disabled:opacity-60"
                    style={{
                        color: "#0B1018",
                        background: "linear-gradient(90deg, #34D399 0%, #10B981 100%)",
                        fontWeight: 500,
                    }}
                >
                    {drafting
                        ? <><Loader2 size={14} strokeWidth={2} className="animate-spin" /> {t("ob.review_assistant.drafting")}</>
                        : <><Wand2 size={14} strokeWidth={1.75} /> {draft ? t("ob.review_assistant.redraft") : t("ob.review_assistant.draft")}</>}
                </button>
            </div>

            {/* Draft area */}
            {(draft || drafting) && (
                <div className="space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="ob-eyebrow !mb-0">
                            {t("ob.review_assistant.draft_label")}
                        </p>
                        {draftMeta && (
                            <p className="text-[10px] uppercase tracking-wider" style={{ color: "var(--ob-text-tertiary)" }}>
                                {draftMeta.word_count} {t("ob.review_assistant.words")} · Claude Sonnet 4.5
                            </p>
                        )}
                    </div>
                    <textarea
                        value={draft}
                        onChange={(e) => onDraftChange(e.target.value)}
                        rows={7}
                        data-testid="ob-review-assistant-draft-textarea"
                        data-insight-id="review-shield.assistant.draft-textarea"
                        data-insight-label="Reply draft"
                        className="w-full rounded-xl p-4 text-base leading-relaxed resize-y"
                        style={{
                            background: "rgba(255,255,255,0.03)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            color: "var(--ob-text-primary)",
                            fontFamily: "Outfit, sans-serif",
                            outline: "none",
                        }}
                        placeholder={drafting ? t("ob.review_assistant.drafting_placeholder") : ""}
                    />
                    <p className="text-xs italic" style={{ color: "var(--ob-text-tertiary)" }}>
                        {t("ob.review_assistant.draft_disclaimer")}
                    </p>
                </div>
            )}

            {/* Operator actions */}
            {draft && (
                <div className="flex items-center gap-2 flex-wrap pt-4" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                    <button
                        type="button"
                        onClick={onCopy}
                        data-testid="ob-review-assistant-copy-btn"
                        data-insight-id="review-shield.assistant.copy-reply"
                        data-insight-label="Copy reply to clipboard"
                        className="inline-flex items-center gap-2 text-sm rounded-full px-4 py-2"
                        style={{
                            color: "var(--ob-text-primary)",
                            background: "rgba(255,255,255,0.06)",
                            border: "1px solid rgba(255,255,255,0.08)",
                        }}
                    >
                        <Copy size={14} strokeWidth={1.75} />
                        {t("ob.review_assistant.copy")}
                    </button>
                    <button
                        type="button"
                        onClick={onMarkResolved}
                        disabled={resolving}
                        data-testid="ob-review-assistant-resolve-btn"
                        data-insight-id="review-shield.action.mark-resolved"
                        data-insight-label="Mark resolved"
                        className="inline-flex items-center gap-2 text-sm rounded-full px-4 py-2 disabled:opacity-60"
                        style={{
                            color: "#A7F3D0",
                            background: "rgba(16,185,129,0.08)",
                            border: "1px solid rgba(16,185,129,0.20)",
                        }}
                    >
                        {resolving
                            ? <Loader2 size={14} strokeWidth={2} className="animate-spin" />
                            : <CheckCircle2 size={14} strokeWidth={1.75} />}
                        {t("ob.review_assistant.mark_resolved")}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ReviewAssistant;
